import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import useDebounce from "../external/ensolvers-core-frontend-mui/hooks/useDebounce";
import AccessRole from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/AccessRole";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import UserService from "../services/UserService";
import { AccessRoleType } from "../types/AccessRoleType";
import { Routes } from "../types/Routes";

const useUsersList = () => {
  const history = useHistory();
  const params = useLocation().search;

  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [selectedUser, setSelectedUser] = useState<EnsolversOpsUser | undefined>(undefined);
  const [search, setSearch] = useState<string>("");
  const debounceSearchTerm = useDebounce(search, 500);
  const [roles, setRoles] = useState({
    roleAdmin: true,
    roleContractor: false,
    roleManager: false,
    roleHR: false,
    roleHiringManager: false,
  });

  useEffect(() => {
    const username = new URLSearchParams(params).get("username");
    if (username !== null) {
      setSearch(username);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => updateCurrentHistoryEntry(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUserStatusChange = async () => {
    if (!selectedUser) return;

    if (selectedUser.externalId) {
      UserService.changeUserStatus(selectedUser.externalId, !selectedUser.enabled)
        .then((response) => {
          if (response.isSuccess()) {
            setSelectedUser(undefined);
            setUpdate(Date.now());
            NotificationService.info("User updated!");
          } else {
            NotificationService.error(response.getMessage());
          }
        })
        .catch((error) => {
          NotificationService.error("Error trying to Enable/Disable a user.");
        });
    }
  };

  const validUser = (user: EnsolversOpsUser): string => {
    if (new Date(user.vacationsComputedSince!) < new Date(user.shortStart)) {
      return "Vacations Computed Since must be equal or greater than Start Date";
    }
    if (user.vacationAccumulation! < 0 || user.vacationAccumulation! > 30) {
      return "Vacations accumulation per year should be positive and less or equal than 30 days";
    }
    return "OK";
  };

  const handleUserCreation = async (user: EnsolversOpsUser) => {
    const userValidationMessage = validUser(user);
    if (userValidationMessage !== "OK") {
      NotificationService.error(userValidationMessage);
    } else {
      UserService.create(user)
        .then((response) => {
          if (response.isSuccess()) {
            NotificationService.info("User created!");
          } else {
            if (response.getMessage().includes("core_user_email_UK")) {
              NotificationService.error("Duplicated mail.");
            } else {
              NotificationService.error("Error trying to create User.");
            }
          }
        })
        .catch((error) => {
          NotificationService.error("Error trying to create User.");
        });
    }
  };
  const handleUserUpdate = async (user: EnsolversOpsUser) => {
    const userValidation = new Date(user.vacationsComputedSince!) >= new Date(user.shortStart);
    userValidation
      ? UserService.update(user)
          .then((response) => {
            if (response.isSuccess()) {
              NotificationService.info("User updated!");
            } else {
              NotificationService.error(response.getMessage());
            }
          })
          .catch((error) => {
            NotificationService.error("Error trying to update user.");
          })
      : NotificationService.error("Error trying to update user. Vacations Computed Since must be equal or greater than Start Date");
  };
  const setRolesForUser = async (user: EnsolversOpsUser) => {
    setRoles({
      roleAdmin: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_ADMIN),
      roleContractor: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_CONTRACTOR),
      roleManager: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_MANAGER),
      roleHR: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_HR),
      roleHiringManager: user.roles.some((role) => role.name.toString() === AccessRoleType.ROLE_HIRING_MANAGER),
    });
  };

  const goPersonalInfoPage = (user: EnsolversOpsUser) => {
    if (!user) return;
    history.push(`${Routes.PROFILE}/${user.externalId}`);
  };

  const updateCurrentHistoryEntry = () => {
    if (search === "") {
      history.replace(`${Routes.USERS}`);
    } else {
      history.replace(`${Routes.USERS}?username=${search}`);
    }
  };

  const getRoles = (roles: AccessRole[]) => {
    let roleString: string = "";
    roles.every((r) => (roleString = roleString.concat(" ", r.name.replace("ROLE_", ""), ",")));
    return roleString.replace(/,$/, "");
  };

  return {
    search,
    setSearch,
    debounceSearchTerm,
    update,
    getRoles,
    setSelectedUser,
    goPersonalInfoPage,
    setRolesForUser,
    selectedUser,
    handleUserStatusChange,
    handleUserCreation,
    handleUserUpdate,
    roles,
  };
};

export default useUsersList;
